import { Type } from '@angular/core';
import { MenuHeaderPreRollComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-pre-roll/menu-header-pre-roll.component';
import { ColWidth } from '../../shared/col-width';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { ProductMenuHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/product-menu-header.component';
import { PrintMenu } from '../print-menu';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import type { SectionWithProducts } from '../section/section-with-products';
import { ColumnGrouping } from '../section/column-grouping';
import { ColumnUtils } from '../../../utils/column-utils';
import { Section } from '../section/section';
import { ProductMenuFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/product-menu-footer.component';
import { BadgeLegendFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/badge-legend-footer/badge-legend-footer.component';
import { PrintFooterLayoutType } from '../../enum/shared/print-footer-layout-type.enum';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { WithinNameCell } from '../../enum/shared/within-name-cell';

export class GridLinesMenu extends PrintMenu {
  private DEFAULT_BORDER_COLOR = '#222222';

  getPageBottomMargin(): string {
    return '0.5in';
  }

  getPageTopMargin(): string {
    return '0.5in';
  }

  getPageLeftMargin(): string {
    return '0.5in';
  }

  getPageRightMargin(): string {
    return '0.5in';
  }

  getThemeClass(): string {
    return 'grid-lines-theme';
  }

  getHeaderType(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderPreRollComponent;
  }

  getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.FirstPage;
  }

  getSectionWrapperClass(...args: any[]): string {
    return 'black-background';
  }

  getShowInlineLabels(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  getMakeHeaderSectionImageFillEntireSection(): boolean {
    return true;
  }

  getShowProductSubtitle(): boolean {
    return false;
  }

  getSectionHeaderUnderlined(): boolean {
    return false;
  }

  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [FONT.Arial.ExtraBold_800, FONT.Arial.Regular_400, FONT.Arial.SemiBold_600];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/wqw0imq.css';
  }

  getProductWrapperStyling(section: SectionWithProducts, sectionRowViewModel: SectionRowViewModel, odd: boolean) {
    const sectionStyling = super.getProductWrapperStyling(section, sectionRowViewModel, odd);
    sectionStyling['margin-bottom'] = this.getSectionBorderMarginSize();
    return sectionStyling;
  }

  getSectionBorderMarginSize(): string {
    const productZoom = this.menuOptions?.productZoom || 1.0;
    const desiredMarginSizePx = 1.0;
    return `${desiredMarginSizePx / productZoom}px`;
  }

  override getThemeSpecifiedColumnViewModels(
    sectionRowViewModels: SectionRowViewModel[],
    rowViewModel: SectionRowViewModel,
    widths: ColWidth[] = this.getColWidths(rowViewModel)
  ): SectionColumnViewModel[] {
    const vms = super.getThemeSpecifiedColumnViewModels(sectionRowViewModels, rowViewModel, widths);
    return vms?.filter(vm => vm.columnType !== SectionColumnProductInfoType.TinySpacer);
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.ProductTitle, 0],
      [SectionColumnProductInfoType.Brand, 1],
      [SectionColumnProductInfoType.Strain, 2],
      [SectionColumnProductInfoType.StrainType, 3],
      [SectionColumnProductInfoType.Badge, 4],
      [SectionColumnProductInfoType.QuantityAndSize, 5],
      [SectionColumnProductInfoType.Quantity, 6],
      [SectionColumnProductInfoType.Size, 7],
      [SectionColumnProductInfoType.VariantType, 8],
      [SectionColumnProductInfoType.Stock, 9],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(10),
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(11),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(12),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(13),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(14),
      [SectionColumnProductInfoType.GradingResults, 15],
      [SectionColumnProductInfoType.VariantPrice, 16],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 17]
    ]);
  }

  getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getGridModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getProductSectionBackgroundColor(section: Section): string {
    return section?.metadata?.productsContainerBackgroundColor ?? this.DEFAULT_BORDER_COLOR;
  }

  getPrintFooterLayoutType(): PrintFooterLayoutType {
    if (this.companyId === 32800658) {
      // capitalCannabisCompanyId
      return this.menuOptions?.printFooterLayout || PrintFooterLayoutType.AllPagesFixed;
    }
    return super.getPrintFooterLayoutType();
  }

  getFooterType(): Type<ProductMenuFooterComponent> {
    if (this.companyId === 32800658) {
      // capitalCannabisCompanyId
      return BadgeLegendFooterComponent;
    }
    return super.getFooterType();
  }
}
